import { useEffect } from "react";

export const Config = {
  Providers: {
    OpenSearch: 'opensearch'
  },
  Layouts: {
    Standard: 'standard',
    Compact: 'compact',
    Horizontal: 'horizontal',
  },
  ViewModes: {
    Cards: 'cards',
  },
  Mapping: {
    Card: {
      Featured: 'featured',
      Badges: 'badges',
      Pretitle: 'pretitle',
      Title: 'title',
      Subtitle: 'subtitle',
      ImageURL: 'imageURL',
      Body: 'body',
      Links: 'links',
    }
  },
  DisplayOptions: {
    ItemsPerPage: {
      DefaultValue: 12,
      DefaultOptions: [12, 24, 36],
    }
  },
  I18n: {
    Filters: {
      BlockLabel: I18n.t('open_search.filters.block_label'),
      SortByLabel: I18n.t('open_search.filters.sort_by_label'),
      ClearAll: I18n.t('open_search.filters.clear_all'),
      RemoveFilter: (field, value) => I18n.t('open_search.filters.remove_filter', {field: field, value: value}),
    },
    Pager: {
      FirstPage: I18n.t('open_search.pagination.first_page'),
      LastPage: I18n.t('open_search.pagination.last_page'),
      NextPage: I18n.t('open_search.pagination.next_page'),
      PreviousPage: I18n.t('open_search.pagination.previous_page'),
      OfTotal: (total) => I18n.t('open_search.pagination.of_total', {total: total}),
    },
    ResultsPerPage: {
      Label: I18n.t('open_search.results_per_page.label'),
      Unit: I18n.t('open_search.results_per_page.unit'),
    },
    Stats: {
      FoundItems: (count) => I18n.t('open_search.stats.results_found', {count: count}),
    },
    Hits: {
      NoResults: I18n.t('open_search.stats.no_results'),
      LearnMore: I18n.t('open_search.hits.learn_more'),
      Featured: I18n.t('open_search.hits.featured'),
    }
  }
}

export function providerConfig(options) {
  const config = {
    alwaysSearchOnInitialLoad: true,
    initialState: {},
    searchQuery: {
      sort: [],
      filters: [],
      result_fields: {},
      facets: {},
      disjunctiveFacets: []
    },
  };

  if (options?.filters) {
    try {
      config.searchQuery.filters = JSON.parse(options.filters);
    } catch (e) {
      console.log('Invalid filters provided');
    }
  }

  if (options?.displayOptions) {
    config.initialState.resultsPerPage = options?.displayOptions?.itemsPerPage || Config.DisplayOptions.ItemsPerPage.DefaultValue;
  }

  // Sorting
  if (options?.sorting && Array.isArray(options.sorting) && options.sorting.length > 0) {
    const firstOption = options.sorting[0];

    config.initialState.sortField = firstOption.field;
    config.initialState.sortDirection = firstOption.direction;
  }

  // Facets
  if (options?.facets && Array.isArray(options.facets)) {
    for (const facetConfig of options.facets) {
      const {field} = facetConfig;
      config.searchQuery.facets[field] = {type: 'value', size: 20, sort: "value"}
      config.searchQuery.disjunctiveFacets.push(field);
    }
  }

  return config;
}

function validateOpenSearchConfig(config) {
  if (!config?.indexes || `${config.indexes}`.trim() === '') {
    return false;
  }

  if (config?.facets && Array.isArray(config.facets)) {
    for (const facet of config.facets) {
      const {field, label} = facet;
      if (!field || !label || `${field}`.trim() === '' || `${label}`.trim() === '') {
        return false;
      }
    }
  }

  return true;
}

function OpenSearchConfigs(providers = []) {
  return providers.filter(validateOpenSearchConfig);
}

function ExtractViewMode(settings) {
  let viewModes = [];
  if (settings?.cardViewModes) {
    const tmp = settings.cardViewModes.map(vm => {
      return {...vm, viewMode: Config.ViewModes.Cards};
    });

    viewModes = [...viewModes, ...tmp];
  }

  return viewModes[0] || null;
}

export function ExtractConfig(settings) {
  const configs = [];

  for (const config of OpenSearchConfigs(settings?.openSearchProviders)) {
    configs.push({...config, displayOptions: settings.displayOptions});
  }

  if (configs.length === 0) {
    return null;
  }

  const config = configs.pop();
  const viewMode = ExtractViewMode(config);
  if (viewMode) {
    config.ViewMode = viewMode;
  }

  if (settings?.compactMode) {
    config.compactMode = settings.compactMode;
  }

  return config;
}

export function uniqueTotalItems(rawResponse) {
  return rawResponse?.aggregations?.unique_count?.total_unique_count?.value || 0;
}

export function connectorConfig(host, indexes, aggregation) {
  const config = {
    host: host,
    index: indexes,
  };

  if (aggregation && `${aggregation}`.trim() !== '') {
    config.connectionOptions = {
      headers: {
        'x-dedup-field': aggregation,
        'X-CSRF-Token': getCsrfToken()
      }
    }
  }

  return config;
}

export function useClickOutside(ref, handler) {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        handler(event.target);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, handler]);
}
